import React, { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import InputField from '@/components/customercenter-ui/common/validateForm/inputField';
import { Checkbox } from '@/components/customercenter-ui/components/ui/checkbox';
import Step3 from '@/components/customercenter-ui/icons/step3';
import { useCheckout } from '@/context/checkoutProvider';
import { useCart } from '@/frontastic';
import { GlobalPageTexts } from '@/helpers/i18n';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { TranslateText } from '@/helpers/utils/siteUtils';
import { DEFAULT_SITE_LANG } from '../../../../../constants/constant';
import { createPaymentSchema } from '../schemas';

type Props = {
  className?: string;
  emailError?: string;
  setEmailError?: any;
  cartHasInsurance?: boolean;
};

const Review: React.FC<Props> = ({ emailError, setEmailError, cartHasInsurance }: Props) => {
  const Texts = TranslateText(GlobalPageTexts, DEFAULT_SITE_LANG);
  const {
    contactInformation,
    setContactInformation,
    isNoChargeOrder,
    checkoutExtraDetails,
    setSendPromotionalEmails: setSendPromotionalEmailsContext,
  } = useCheckout();
  const [email, setEmail] = useState(contactInformation?.email ?? '');
  const { updateCart } = useCart(false);
  const [sendPromotionalEmails, setSendPromotionalEmails] = useState(checkoutExtraDetails?.sendPromotionalEmails);

  const PaymentSchema = createPaymentSchema({ cartHasInsurance: cartHasInsurance });

  useEffect(() => {
    setSendPromotionalEmailsContext(sendPromotionalEmails);
  }, [sendPromotionalEmails]);

  useEffect(() => {
    setSendPromotionalEmails(checkoutExtraDetails?.sendPromotionalEmails);
  }, [checkoutExtraDetails?.sendPromotionalEmails]);

  const {
    register,
    reset,
    setValue,
    formState: { errors, isValid },
    clearErrors,
  } = useForm({
    resolver: zodResolver(PaymentSchema),
    defaultValues: { email: contactInformation?.email ?? '' },
    mode: 'onChange',
  });

  const handleSaveCardCheckboxChange = () => {
    setSendPromotionalEmails((prev) => !prev);
    reset();
    clearErrors();
    setEmailError('');
  };

  useEffect(() => {
    if (!cartHasInsurance && errors) {
      reset();
      clearErrors();
      setEmailError('');
    }
  }, [cartHasInsurance]);

  useEffect(() => {
    if (contactInformation?.email) {
      setEmail(contactInformation.email);
      setValue('email', contactInformation.email, { shouldValidate: true });
    }
  }, [contactInformation?.email, setValue, cartHasInsurance]);

  const handleEmailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailError('');
    const newEmail = e.target.value;
    setEmail(newEmail);
    setContactInformation({
      ...contactInformation,
      email: newEmail,
    });

    if (isValid) {
      await updateCart({
        account: {
          email: newEmail,
        },
      });
    }
  };
  return (
    <section className="border-t border-foggyGray lg:rounded-md lg:border lg:shadow-md">
      <section className="ml-4 flex flex-row pt-6 lg:ml-0 lg:pt-0">
        <section className="lg:py-6 lg:pl-26">
          <Step3 />
        </section>
        <section className="">
          <p className="pl-3 text-[22px] font-bold lg:pt-7">{Texts?.review}</p>
        </section>
      </section>

      {isNoChargeOrder ? (
        <section className=" px-16 pb-6 text-lg">
          <article className="">
            <strong className="pt-8 text-lg font-bold leading-6 lg:pt-7">
              {mapLocaleToMeaningfulFormat().paymentMethod}
            </strong>
            <p className="overflow-hidden text-ellipsis pb-9 pr-6 pt-2.5 text-base font-normal lg:pb-0 lg:pr-3">
              No Charge
            </p>
          </article>
        </section>
      ) : (
        <section className=" px-16 pb-6 text-lg">
          <p className="my-2">{Texts?.reviewText1}</p>
          <p className="my-6 text-displayRed">{Texts?.reviewText2}</p>
          <p className="my-6">{Texts?.reviewText3}</p>
          <section className="w-1/2">
            <InputField
              id="emailAddress"
              htmlFor="emailAddress"
              label="email"
              onChange={handleEmailChange}
              items={mapLocaleToMeaningfulFormat().email}
              register={register}
              classname="w-full"
              value={email}
              disabled={checkoutExtraDetails?.sendPromotionalEmails ? true : false}
              errors={errors}
            />
          </section>
          <section className="my-6 text-displayRed">{emailError}</section>
          {!cartHasInsurance && (
            <section className="mb-4 flex items-center space-x-2 pt-4">
              <Checkbox
                id="1323"
                className=""
                onCheckedChange={handleSaveCardCheckboxChange}
                defaultChecked={sendPromotionalEmails}
                checked={sendPromotionalEmails}
                disabled={contactInformation?.email ? true : false}
              />
              <label htmlFor="1323" className="px-0.5 text-base font-bold leading-6 ">
                No Email Address Provided
              </label>
            </section>
          )}
        </section>
      )}
    </section>
  );
};

export default Review;
